import {
  AIContext,
  AIPersonalisationHook,
  AISummaryContainerFloaterPortal,
  AI_BOT_STATE,
  AI_CONFIG,
  AccessControl,
  DraggableContainer,
  EventEmitter,
  EventEmitterWithIndex,
  GLOBAL_AI_BOT_STATE,
  Portal,
  getTabModule,
  useUserMSPCheckHook,
  withErrorBoundary,
  withPlanFeatureGating
} from '@nile/components';
import { useGlobalChatHook } from '@nile/components/src/Components/GlobalChatHook';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import { getTabUrlNew } from '@nile/mangifera/src/Pebbles/TabManager';
import Tippy from '@tippyjs/react';
import { isEmpty } from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import 'tippy.js/animations/scale-extreme.css';
import 'tippy.js/dist/tippy.css'; // default styles
import GlobalAIBot from './components/GlobalAIBot';
import './styles/ai-styles.scss';
import { SVGIcon } from '@superops/super-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAIStore } from '@nile/components/src/Components/AI/store/aiStore.tsx';
import { AIOnboardingHook } from './hooks/AIOnboardingHook.tsx';
import { useOnboarding } from './store/OnboardingStore.tsx';
import { useAIRecommendedSolutionsStore } from '@nile/components/src/Components/AI/store/aiRecommendedSolutionsStore';
import { useSimilarTicketsStore } from '@nile/components/src/Components/AI/store/similarTicketsStore';
import { useTicketTriageStore } from '@nile/components/src/Components/AI/store/ticketTriageStore.tsx';
import useAINotificationHook from './hooks/useAINotificationHook';
import { DisplayText } from '@superops/ui-design-system';

const ONBOARDING_MODULE_CONFIG = {
  TICKET: [
    'TICKET_CONVERSATION_SUMMARIZE',
    'SIDE_CONVERSATION_COMPOSE',
    'WORKLOG_SUMMARIZATION',
    'WORKLOG_NOTE_COMPOSE',
    'RECOMMENDED_SOLUTION',
    'SIMILAR_TICKETS',
    'KNOWLEDGE_BASE',
    'TICKET_DEFLECTION',
    'PROMPT_BUILDER'
  ],
  WORKSPACE: ['TICKET_TRIAGE'],
  CREATE_SCRIPT: ['SCRIPT_GENERATION'],
  RMM_HOME: ['PATCH_AGENT'],
  REPORTS_CHARTS_HOME: ['TICKETS_SUMMARY_BY_CATEGORY']
};

const DraggableWrapper = ({
  children,
  setIsDragging,
  elementRef,
  onDrag,
  onDragEnd,
  onDragStart
}) => {
  const dragging = (val) => {
    if (setIsDragging) {
      setIsDragging(val);
    }
  };
  return (
    <DraggableContainer
      handle=".handle"
      setIsDragging={dragging}
      defaultPosition="bottom-right"
      elementRef={elementRef}
      onDragEnd={onDragEnd}
      onDrag={onDrag}
      negativeXOffset={15}
      negativeYOffset={-5}
    >
      {children}
    </DraggableContainer>
  );
};

const DRAG_TIMEOUT = 50;
const GREETINGS_TIMEOUT = 1000;
const RESET_DATA_TIMEOUT = 200;
const POPOVER_CLOSE_TIMEOUT = 200;
const ONBOARDING_TIMEOUT = 500;

const GlobalAI = () => {
  const tabContext = useContext(TabContext);

  const aiNotificationHook = useAINotificationHook();
  const { notifications, getInitialNotificationCount } = aiNotificationHook;
  const count = notifications?.length || 0;

  const aiData = useAIStore((s) => s.aiData);
  const setAIData = useAIStore((s) => s.setAIData);
  const resetData = useAIStore((s) => s.resetData);

  const aiDataRef = useRef(aiData);
  aiDataRef.current = aiData;

  const [aiOptions, setAiOptions] = useState(null);
  const [onboardMessage, setOnboardMessage] = useState(null);
  const flags = useFlags();
  const aiOptionsRef = useRef(null);
  const onboardMessageRef = useRef(null);
  aiOptionsRef.current = aiOptions;
  onboardMessageRef.current = onboardMessage;

  const [optionsPopoverActive, setOptionsPopoverActive] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const draggableRef = React.useRef(null);
  const isDraggingRef = React.useRef(false);
  const tippyRef = React.useRef(null);
  const aiOnboardingHook = AIOnboardingHook();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const onboardingStore = useOnboarding();
  const aiRecommendedSolutionsStore = useAIRecommendedSolutionsStore();
  const similarTicketsStore = useSimilarTicketsStore();
  const ticketTriageStore = useTicketTriageStore();

  const { getGlobalConversations } = useGlobalChatHook();

  const getModule = () => {
    const allUrl = getTabUrlNew({ getAllURL: true });
    const moduleData = getTabModule(pathName, allUrl);
    const module = moduleData?.module;
    return module;
  };

  function repositionPopup() {
    if (tippyRef?.current?._tippy) {
      tippyRef.current._tippy.popperInstance.forceUpdate();
    }
  }

  const dragTimeoutRef = useRef(null);
  const closeTimeOutRef = useRef(null);

  // Timer for smooth animation for greeting
  const greetingTimeoutRef = useRef(null);

  // Timer for smooth animation while closing the popup and reset data
  const resetDataTimer = useRef(null);

  // Timer for smooth animation open for onboarding
  const onboardingTimeoutRef = useRef(null);

  const clearTimers = () => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    if (closeTimeOutRef.current) {
      clearTimeout(closeTimeOutRef.current);
    }
    if (greetingTimeoutRef.current) {
      clearTimeout(greetingTimeoutRef.current);
    }
    if (resetDataTimer.current) {
      clearTimeout(resetDataTimer.current);
    }
    if (onboardingTimeoutRef.current) {
      clearTimeout(onboardingTimeoutRef.current);
    }
  };

  const onDrag = () => {
    if (!isDraggingRef.current) {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
      dragTimeoutRef.current = setTimeout(() => {
        setDragging(true);
        isDraggingRef.current = true;
      }, DRAG_TIMEOUT);
    }
  };

  const onDragEnd = () => {
    if (dragTimeoutRef.current) {
      clearTimeout(dragTimeoutRef.current);
    }
    dragTimeoutRef.current = setTimeout(() => {
      setDragging(false);
      isDraggingRef.current = false;
    }, DRAG_TIMEOUT);
    if (optionsPopoverActive) {
      repositionPopup();
    }
  };

  const aiPersonalisationHook = AIPersonalisationHook();

  const aiPersonalisationData = aiPersonalisationHook?.aiPersonalisationData;

  const [riveState, setRiveState] = useState(null);
  const isBotStateExit = riveState === GLOBAL_AI_BOT_STATE.exit;

  const isMSP = useUserMSPCheckHook();

  const location = useLocation();
  const history = useHistory();

  const pathName = location.pathname;

  const getAIOptionsBasedOnUrl = (configData) => {
    const globalConversations = getGlobalConversations();
    if (configData?.url && pathName) {
      // Check based on url;
      const urlData = configData?.url.find((el) => pathName?.includes(el?.url));
      const isFlagEnabled = urlData?.flag ? flags?.[urlData?.flag] : true;
      if (urlData && isFlagEnabled) {
        setAiOptions(urlData?.options);
        setOnboardMessage(urlData?.onboardMessage || null);
        setRiveState(GLOBAL_AI_BOT_STATE.entry);
      } else {
        setAiOptions(null);
        setOnboardMessage(null);
        if (
          riveState &&
          riveState !== GLOBAL_AI_BOT_STATE.exit &&
          isEmpty(globalConversations)
        ) {
          setRiveState(
            count && count > 0
              ? GLOBAL_AI_BOT_STATE.entry
              : GLOBAL_AI_BOT_STATE.exit
          );
        }
      }
    }
  };

  const getOptionsBasedOnModule = (configData) => {
    const globalConversations = getGlobalConversations();
    setOnboardMessage(configData?.onboardMessage || null);
    const isFlagEnabled = configData?.flag ? flags?.[configData?.flag] : true;
    if (isFlagEnabled && configData?.options) {
      // Check based on options
      setAiOptions(configData?.options || null);
      setRiveState(GLOBAL_AI_BOT_STATE.entry);
    } else {
      setAiOptions(null);
      if (
        riveState &&
        riveState !== GLOBAL_AI_BOT_STATE.exit &&
        isEmpty(globalConversations)
      ) {
        setRiveState(
          count && count > 0
            ? GLOBAL_AI_BOT_STATE.entry
            : GLOBAL_AI_BOT_STATE.exit
        );
      }
    }
  };

  const getAiOptions = () => {
    const module = getModule();
    const configData = AI_CONFIG?.[module];
    const globalConversations = getGlobalConversations();

    if (!isEmpty(configData)) {
      if (configData?.url) {
        getAIOptionsBasedOnUrl(configData);
      } else {
        getOptionsBasedOnModule(configData);
      }
    } else {
      setAiOptions(null);
      setOnboardMessage(null);
      if (riveState && isEmpty(globalConversations)) {
        setRiveState(
          count && count > 0
            ? GLOBAL_AI_BOT_STATE.entry
            : GLOBAL_AI_BOT_STATE.exit
        );
      }
    }
  };

  const openAIFeat = (params) => {
    if (aiDataRef.current) {
      resetData();
      setTimeout(() => {
        openPopover(params);
        onboardingStore.resetOnBoarding();
        aiRecommendedSolutionsStore?.resetRecommendedSolutionsData();
        similarTicketsStore?.resetSimilarTicketsData();
        ticketTriageStore?.resetTicketTriageData();
      }, 10);
      return;
    }
    openPopover(params);
    onboardingStore.resetOnBoarding();

    aiRecommendedSolutionsStore?.resetRecommendedSolutionsData();
    similarTicketsStore?.resetSimilarTicketsData();
    ticketTriageStore?.resetTicketTriageData();
  };

  const openPopover = (params) => {
    setOptionsPopoverActive(true);
    setRiveState(GLOBAL_AI_BOT_STATE.Arrow_Entry);
    if (params) {
      setAIData(params);
    }
    if (!params && isEmpty(aiData)) {
      // TODO: ASH: REMOVE TEMP_FIX FOR listenDefaultAgent
      EventEmitterWithIndex.dispatch('listenDefaultAgent');
    }
  };

  const onAIClose = () => {
    if (closeTimeOutRef.current) {
      clearTimeout(closeTimeOutRef.current);
    }
    closeTimeOutRef.current = setTimeout(() => {
      setOptionsPopoverActive(false);
    }, POPOVER_CLOSE_TIMEOUT);
  };

  const closeAIAndResetData = () => {
    onAIClose();
    onboardingStore.resetOnBoarding();
    setShowOnboarding(false);
    if (resetDataTimer.current) {
      clearTimeout(resetDataTimer.current);
    }
    resetDataTimer.current = setTimeout(() => {
      resetData();
    }, RESET_DATA_TIMEOUT);
  };

  const onBack = () => {
    resetData();
    setRiveState(GLOBAL_AI_BOT_STATE.Bot_Entry);
  };

  const closePopover = () => {
    EventEmitter.dispatch('AISummaryClosed');
    setRiveState(GLOBAL_AI_BOT_STATE.Bot_Entry);
  };

  const togglePopover = (override) => {
    if (!isDraggingRef.current || override) {
      isDraggingRef.current = false;
      if (
        !isEmpty(aiOptions) ||
        override ||
        onboardMessage ||
        (count && count > 0)
      ) {
        const updatedState = !optionsPopoverActive;
        if (updatedState) {
          openPopover();
        } else {
          closePopover();
          closeAIAndResetData();
        }
      } else {
        closePopover();
        closeAIAndResetData();
      }
    }
  };

  const checkGreetings = () => {
    // TODO: Find a way to get greeting data using common function
    const module = getModule();
    if (
      !aiPersonalisationData?.ticketGreetingsComplete &&
      module === 'TICKET'
    ) {
      if (greetingTimeoutRef.current) {
        clearTimeout(greetingTimeoutRef.current);
      }
      greetingTimeoutRef.current = setTimeout(() => {
        // We need to pass true to props as options won't be available on initial state
        setShowGreeting(true);
        togglePopover(true);
      }, GREETINGS_TIMEOUT);
    }
  };

  const startOnboarding = (agentName) => {
    if (onboardingTimeoutRef.current) {
      clearTimeout(onboardingTimeoutRef.current);
    }
    onboardingTimeoutRef.current = setTimeout(() => {
      aiOnboardingHook?.startOnboarding(agentName);
      setShowOnboarding(true);
      togglePopover(true);
    }, ONBOARDING_TIMEOUT);
  };

  const onTicketGlobalConversationClose = () => {
    // Temp fix using ref. Check why aiOptions is null
    if (isEmpty(aiOptionsRef.current) && isEmpty(onboardMessageRef.current)) {
      setRiveState(
        count && count > 0
          ? GLOBAL_AI_BOT_STATE.entry
          : GLOBAL_AI_BOT_STATE.exit
      );
    }
  };

  const removeParams = () => {
    const params = new URLSearchParams(location.search);
    const agentNameForOnboarding = params.get('agentOnboarding');

    if (agentNameForOnboarding) {
      // Delete the param
      params.delete('agentOnboarding');
      history?.replace({
        search: ''
      });
      // Remove params from history
      // tabContext.updateTab({
      //   module: tabContext?.activeTab?.module,
      //   url: pathName
      // });
    }
  };

  const checkOnboarding = () => {
    const params = new URLSearchParams(location.search);
    const agentNameForOnboarding = params.get('agentOnboarding');

    if (agentNameForOnboarding) {
      const currentModule = tabContext?.activeTab?.module;
      if (
        ONBOARDING_MODULE_CONFIG?.[currentModule]?.includes(
          agentNameForOnboarding
        )
      ) {
        if (
          aiOnboardingHook?.isActive &&
          aiOnboardingHook?.currentFeature === agentNameForOnboarding
        ) {
          return true;
        }
        startOnboarding(agentNameForOnboarding);
        removeParams();
        return true;
      }
    } else {
      const currentOnBoardingPresent = aiOnboardingHook?.isActive;
      if (currentOnBoardingPresent) {
        if (onboardingTimeoutRef.current) {
          clearTimeout(onboardingTimeoutRef.current);
        }
        onboardingTimeoutRef.current = setTimeout(() => {
          setShowOnboarding(true);
          togglePopover(true);
        }, ONBOARDING_TIMEOUT);
      }
      return currentOnBoardingPresent || false;
    }
    return false;
  };

  const checkOpenPopOverOnMount = () => {
    const isOnboarding = checkOnboarding();
    if (isOnboarding) {
      onboardingTimeoutRef.current = setTimeout(() => {
        togglePopover(true);
      }, ONBOARDING_TIMEOUT);
      return;
    }
    if (aiData) {
      togglePopover(true);
    } else {
      checkGreetings();
    }
  };

  const checkNewAgentOnboaring = async () => {
    const queryString = window.location.hash.split('?')[1];
    const params = new URLSearchParams(queryString);
    let agentConstantForOnboarding = null;
    if (params) {
      const appStateParam = params.get('appState');
      const appState = JSON.parse(decodeURIComponent(appStateParam));
      agentConstantForOnboarding = appState?.getAgentOnboarding;
    }

    if (agentConstantForOnboarding) {
      const onboardingEntityData = await aiOnboardingHook?.getOnboardingEntity(
        agentConstantForOnboarding,
        { getAIFeaturesList: true }
      );

      if (onboardingEntityData) {
        aiOnboardingHook?.redirectToEntity({
          entity: onboardingEntityData.entity,
          entityId: onboardingEntityData.entityId,
          constant: agentConstantForOnboarding
        });
      }
    }
  };

  useEffect(() => {
    if (isMSP) {
      setOptionsPopoverActive(false);
      getAiOptions();
      // When URL is only changed we'll call this
      checkOpenPopOverOnMount();
      checkNewAgentOnboaring();
    } else {
      setRiveState(
        count && count > 0
          ? GLOBAL_AI_BOT_STATE.entry
          : GLOBAL_AI_BOT_STATE.exit
      );
    }
  }, [pathName]); // Add dependencies

  const showMonicaAI = (configData) => {
    if (!isEmpty(configData)) {
      if (configData?.url) {
        getAIOptionsBasedOnUrl(configData);
      } else {
        getOptionsBasedOnModule(configData);
      }
    } else {
      setAiOptions(null);
      setOnboardMessage(null);
    }
  };

  const hideMonicaAi = () => {
    const globalConversations = getGlobalConversations();
    setAiOptions(null);
    setOnboardMessage(null);
    if (
      riveState &&
      // Calling exit when riveState is null is triggering Monia AI
      riveState !== GLOBAL_AI_BOT_STATE.exit &&
      isEmpty(globalConversations)
    ) {
      setRiveState(
        count && count > 0
          ? GLOBAL_AI_BOT_STATE.entry
          : GLOBAL_AI_BOT_STATE.exit
      );
    }
  };

  const getMonicaAINotifications = async () => {
    const response = await getInitialNotificationCount();
  };

  const updateRiveStateBasedOnCount = () => {
    if (count && count > 0) {
      if (riveState !== GLOBAL_AI_BOT_STATE.exit) {
        setRiveState(GLOBAL_AI_BOT_STATE.entry);
      }
    }
  };

  useEffect(() => {
    updateRiveStateBasedOnCount();
  }, [count]);

  useEffect(() => {
    // TODO - AI ONBOARDING Call API
    // APi response with URL and feature
    // Redirect to URL
    // Set the store with feature
    // const featureName = 'RECOMMENDED_SOLUTION';
    // aiOnboardingHook?.startOnboarding(featureName);

    EventEmitter.subscribe('tabChange', onAIClose);
    EventEmitter.subscribe('closeTab', onAIClose);
    EventEmitter.subscribe('openAISummary', openAIFeat);
    EventEmitter.subscribe(
      'globalConversationClosed',
      onTicketGlobalConversationClose
    ); // Check why this event is not captured it's emitted from GlobalChatBox
    EventEmitter.subscribe(
      'ticketGlobalConversationClosed',
      onTicketGlobalConversationClose
    );
    EventEmitter.subscribe('showMonicaAI', showMonicaAI);
    EventEmitter.subscribe('hideMonicaAi', hideMonicaAi);
    EventEmitter.subscribe('showOnboardingUI', checkOnboarding);
    getMonicaAINotifications();
    return function cleanup() {
      EventEmitter.unsubscribe('openAISummary');
      EventEmitter.unsubscribe('globalConversationClosed');
      EventEmitter.unsubscribe('ticketGlobalConversationClosed');
      EventEmitter.unsubscribe('showMonicaAI');
      EventEmitter.unsubscribe('hideMonicaAi');
      EventEmitter.unsubscribe('tabChange');
      EventEmitter.unsubscribe('closeTab');
      EventEmitter.unsubscribe('showOnboardingUI');
      clearTimers();
    };
  }, []);

  const botClassNames = [
    'app-ai-bot position-relative',
    !isBotStateExit && dragging ? 'cursor-move' : 'pointer',
    'ai-rive-bot-wrapper handle visibilityContainer position-relative flex actr jctr g5',
    isBotStateExit && 'pointer-events-none cursor-default exit'
  ]
    .filter(Boolean)
    .join(' ');

  if (riveState && isMSP) {
    return (
      <Portal>
        <DraggableWrapper
          elementRef={draggableRef}
          onDragEnd={onDragEnd}
          onDrag={onDrag}
        >
          <AIContext.Provider
            value={{
              showGreeting,
              setShowGreeting,
              showOnboarding,
              setShowOnboarding,
              repositionPopup,
              aiData,
              aiOnboardingHook: aiOnboardingHook
            }}
          >
            <Tippy
              delay={100}
              interactive
              placement="top-end"
              arrow={false}
              allowHTML
              trigger="manual"
              theme="light"
              duration={150}
              offset={[-80, 10]}
              animation="scale-extreme"
              content={
                <>
                  {optionsPopoverActive && (
                    <AISummaryContainerFloaterPortal
                      aiOptions={aiOptions}
                      onClose={onAIClose}
                      aiData={aiData}
                      onboardMessage={onboardMessage}
                      onBack={onBack}
                    />
                  )}
                </>
              }
              visible={optionsPopoverActive}
              className="ai-popup-tippy"
              ref={(node) => {
                tippyRef.current = node;
              }}
              onUpdate={repositionPopup}
            >
              <div
                className={botClassNames}
                onClick={() => togglePopover()}
                ref={draggableRef}
              >
                {count && count > 0 ? (
                  <div className="ai-notification-dot"></div>
                ) : (
                  <></>
                )}
                <div
                  className={
                    isBotStateExit
                      ? 'hiddenActions pointer-events-none cursor-default'
                      : 'opacity5 ai-move-indicator visibilityActions position-relative cursor-move'
                  }
                >
                  <SVGIcon name="Reorder" />
                </div>
                <GlobalAIBot riveState={riveState} />
              </div>
            </Tippy>
          </AIContext.Provider>
        </DraggableWrapper>
      </Portal>
    );
  }
  return <></>;
};

export default withPlanFeatureGating(withErrorBoundary(GlobalAI), {
  feature: 'AI_FEATURES',
  mode: 'hide',
  id: 'GlobalAI'
});
